/**
 * Gerador único de colunas de período (dia / semana / mês) e pro-rata de horas.
 *
 * Nasceu para a tela Equipe & Alocação e substitui as três cópias privadas de
 * getWeeksBetween que existiam em capacityService, taskWorkloadService e no MCP.
 * Regras do handoff (design_handoff_equipe_alocacao/README.md):
 *  - a SEMANA é a base do dado, ancorada em segunda-feira;
 *  - DIA e MÊS derivam de datas reais, nunca de fator fixo;
 *  - colunas de dia são apenas dias ÚTEIS do projeto (workDays);
 *  - coluna de mês agrega as semanas cuja segunda-feira cai no mês.
 *
 * O pro-rata corrige a mentira de borda: uma alocação que intersecta 1 dia útil
 * da semana conta 1/5 das horas, não a semana cheia.
 */

import { isDateHoliday } from './dateUtils';

export type PeriodZoom = 'day' | 'week' | 'month';

export interface PeriodColumn {
  /** Chave estável (ISO da data inicial + zoom). */
  key: string;
  zoom: PeriodZoom;
  /** Início do período (00:00 local). */
  start: Date;
  /** Fim INCLUSIVO do período (última data coberta). */
  end: Date;
  /** Quantas semanas-base o período representa (1 para dia/semana). */
  weeksCount: number;
}

const DEFAULT_WORK_DAYS = [1, 2, 3, 4, 5];

/**
 * 'YYYY-MM-DD' vira data LOCAL, nunca UTC. `new Date('2026-08-18')` em BRT
 * resolve para 17/08 21:00 e desloca toda a grade em um dia; esta é a mesma
 * classe de bug que já jogou signups na coorte errada no BI.
 */
function parseLocal(date: Date | string): Date {
  if (typeof date === 'string') {
    const m = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return new Date(date);
}

export function getMonday(date: Date | string): Date {
  const d = parseLocal(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function atMidnight(date: Date | string): Date {
  const d = parseLocal(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toKey(zoom: PeriodZoom, start: Date): string {
  const y = start.getFullYear();
  const m = String(start.getMonth() + 1).padStart(2, '0');
  const day = String(start.getDate()).padStart(2, '0');
  return `${zoom}:${y}-${m}-${day}`;
}

/** Semanas segunda→domingo cobrindo o range (a última pode passar do fim pedido). */
export function getWeekColumns(start: Date | string, end: Date | string): PeriodColumn[] {
  const cols: PeriodColumn[] = [];
  let current = getMonday(start);
  const endDate = atMidnight(end);
  while (current <= endDate) {
    cols.push({
      key: toKey('week', current),
      zoom: 'week',
      start: new Date(current),
      end: addDays(current, 6),
      weeksCount: 1,
    });
    current = addDays(current, 7);
  }
  return cols;
}

/** Dias ÚTEIS do range (workDays do projeto; padrão seg–sex). */
export function getDayColumns(
  start: Date | string,
  end: Date | string,
  workDays: number[] = DEFAULT_WORK_DAYS
): PeriodColumn[] {
  const valid = workDays.length > 0 ? workDays : DEFAULT_WORK_DAYS;
  const cols: PeriodColumn[] = [];
  let current = atMidnight(start);
  const endDate = atMidnight(end);
  while (current <= endDate) {
    if (valid.includes(current.getDay())) {
      cols.push({
        key: toKey('day', current),
        zoom: 'day',
        start: new Date(current),
        end: new Date(current),
        weeksCount: 1,
      });
    }
    current = addDays(current, 1);
  }
  return cols;
}

/** Meses = agregação das semanas cuja SEGUNDA cai no mês (regra do handoff). */
export function getMonthColumns(start: Date | string, end: Date | string): PeriodColumn[] {
  const weeks = getWeekColumns(start, end);
  const byMonth = new Map<string, PeriodColumn[]>();
  weeks.forEach(w => {
    const k = `${w.start.getFullYear()}-${String(w.start.getMonth() + 1).padStart(2, '0')}`;
    const list = byMonth.get(k) || [];
    list.push(w);
    byMonth.set(k, list);
  });
  return Array.from(byMonth.values()).map(list => ({
    key: toKey('month', list[0].start),
    zoom: 'month' as const,
    start: list[0].start,
    end: list[list.length - 1].end,
    weeksCount: list.length,
  }));
}

export function generateColumns(
  zoom: PeriodZoom,
  start: Date | string,
  end: Date | string,
  workDays: number[] = DEFAULT_WORK_DAYS
): PeriodColumn[] {
  if (zoom === 'day') return getDayColumns(start, end, workDays);
  if (zoom === 'month') return getMonthColumns(start, end);
  return getWeekColumns(start, end);
}

/** Dias úteis dentro de um intervalo inclusivo, já fora feriados do projeto. */
export function businessDaysBetween(
  start: Date | string,
  end: Date | string,
  workDays: number[] = DEFAULT_WORK_DAYS,
  holidays: string[] = []
): number {
  const valid = workDays.length > 0 ? workDays : DEFAULT_WORK_DAYS;
  let count = 0;
  let current = atMidnight(start);
  const endDate = atMidnight(end);
  while (current <= endDate) {
    if (valid.includes(current.getDay()) && !isDateHoliday(current, holidays)) count++;
    current = addDays(current, 1);
  }
  return count;
}

export interface DateRangeLike {
  start_date: string;
  end_date: string;
  hours_per_week: number | string;
}

/**
 * Horas de uma alocação dentro de uma coluna, com pro-rata por dias úteis de
 * interseção. Alocação de 20h/sem que só toca 1 dia útil da semana = 4h.
 */
export function allocationHoursInColumn(
  alloc: DateRangeLike,
  col: PeriodColumn,
  workDays: number[] = DEFAULT_WORK_DAYS,
  holidays: string[] = []
): number {
  const valid = workDays.length > 0 ? workDays : DEFAULT_WORK_DAYS;
  const aStart = atMidnight(alloc.start_date);
  const aEnd = atMidnight(alloc.end_date);
  if (aStart > col.end || aEnd < col.start) return 0;
  const overlapStart = aStart > col.start ? aStart : col.start;
  const overlapEnd = aEnd < col.end ? aEnd : col.end;
  const overlapDays = businessDaysBetween(overlapStart, overlapEnd, valid, holidays);
  if (overlapDays === 0) return 0;
  const hoursPerDay = (parseFloat(String(alloc.hours_per_week)) || 0) / Math.max(1, valid.length);
  return Math.round(hoursPerDay * overlapDays * 10) / 10;
}

/** A ausência intersecta a coluna? Serve para marcar a célula, não para zerá-la. */
export function rangeTouchesColumn(
  range: { start_date: string; end_date: string },
  col: PeriodColumn
): boolean {
  const s = atMidnight(range.start_date);
  const e = atMidnight(range.end_date);
  return s <= col.end && e >= col.start;
}

/**
 * Dias ÚTEIS da coluna cobertos por ausência.
 *
 * Antes, qualquer interseção zerava a coluna inteira: um único dia de férias na
 * segunda apagava a semana toda de capacidade da pessoa. Aqui o desconto é
 * proporcional, e o Set de dias impede que duas ausências sobrepostas descontem
 * o mesmo dia duas vezes.
 */
export function absentBusinessDaysInColumn(
  absences: ReadonlyArray<{ start_date: string; end_date: string }>,
  col: PeriodColumn,
  workDays: number[] = DEFAULT_WORK_DAYS,
  holidays: string[] = []
): number {
  if (!absences || absences.length === 0) return 0;
  const valid = workDays.length > 0 ? workDays : DEFAULT_WORK_DAYS;
  const dias = new Set<number>();
  for (const ausencia of absences) {
    const inicio = atMidnight(ausencia.start_date);
    const fim = atMidnight(ausencia.end_date);
    if (inicio > col.end || fim < col.start) continue;
    let atual = inicio > col.start ? inicio : col.start;
    const ate = fim < col.end ? fim : col.end;
    while (atual <= ate) {
      if (valid.includes(atual.getDay()) && !isDateHoliday(atual, holidays)) {
        dias.add(atual.getTime());
      }
      atual = addDays(atual, 1);
    }
  }
  return dias.size;
}

/**
 * Capacidade de um recurso numa coluna.
 *
 * Fórmula única para os três zooms: capacidade diária vezes os dias realmente
 * disponíveis. Substitui o par de casos que multiplicava por `weeksCount` sem
 * olhar o calendário, e que por isso não sabia o que era feriado.
 *
 * Vale registrar que isto NÃO muda número nenhum em projeto com calendário
 * padrão e sem feriado: coluna de mês cobre semanas inteiras, então
 * `diária × (weeksCount × diasDaSemana)` é idêntico a `semanal × weeksCount`.
 * Quem vê diferença é exatamente quem configurou o calendário, que era o ponto.
 *
 * `weeklyCapacity` já deve chegar limitada pelo calendário do projeto: ver
 * `capacidadeSemanalEfetiva` em `capacityService`.
 */
export function capacityInColumn(
  weeklyCapacity: number,
  col: PeriodColumn,
  workDays: number[] = DEFAULT_WORK_DAYS,
  holidays: string[] = [],
  diasAusentes: number = 0
): number {
  const valid = workDays.length > 0 ? workDays : DEFAULT_WORK_DAYS;
  const uteis = businessDaysBetween(col.start, col.end, valid, holidays);
  const disponiveis = Math.max(0, uteis - diasAusentes);
  const diaria = weeklyCapacity / Math.max(1, valid.length);
  return Math.round(diaria * disponiveis * 10) / 10;
}
